import { useState } from 'react';
import { useT } from '../i18n/translations';
import { loginRequest, verifyOtpRequest, resendOtpRequest } from '../authApi';
import { saveAuth } from '../authStorage';
import './MeetingRoomLogin.css';

const OTP_LENGTH = 6;

/**
 * MeetingRoomLogin
 *
 * Shown on the meeting-room page when the visitor isn't logged in yet.
 * Any registered member (secretary, board member, shareholder…) can sign in
 * here with their normal account credentials; once authenticated, the
 * caller re-checks room access via onSuccess().
 */
export default function MeetingRoomLogin({ onSuccess, onCancel }) {
  const t = useT();
  const [form, setForm] = useState({ username: '', password: '' });
  const [otp, setOtp] = useState(null); // { challengeId, maskedPhone, otpCode, displayOtpCode }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleChange(e) {
    setError('');
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      setError(t('auth.fillRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await loginRequest({ userName: form.username.trim(), password: form.password });
      if (data?.user?.isActive === false) {
        throw new Error(t('auth.accountPending'));
      }
      if (data?.requiresOtp) {
        const serverOtp = typeof data?.otpCode === 'string' ? data.otpCode.trim() : '';
        setOtp({
          challengeId: data.otpChallengeId ?? '',
          maskedPhone: data.otpMaskedPhone ?? '',
          otpCode: serverOtp.length === OTP_LENGTH ? serverOtp : '',
          displayOtpCode: serverOtp,
        });
        return;
      }
      saveAuth(data);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleOtpSubmit(e) {
    e.preventDefault();
    if (!otp?.challengeId || otp.otpCode.length !== OTP_LENGTH) {
      setError(t('auth.enterOtp'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await verifyOtpRequest({ otpChallengeId: otp.challengeId, otpCode: otp.otpCode });
      saveAuth(data);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!otp?.challengeId) return;
    setError('');
    try {
      const data = await resendOtpRequest({ otpChallengeId: otp.challengeId });
      const serverOtp = typeof data?.otpCode === 'string' ? data.otpCode.trim() : '';
      setOtp({
        ...otp,
        challengeId: data.otpChallengeId ?? otp.challengeId,
        otpCode: serverOtp.length === OTP_LENGTH ? serverOtp : '',
        displayOtpCode: serverOtp,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    }
  }

  return (
    <div className="rlogin">
      <div className="rlogin__card">
        <h2 className="rlogin__title">{t('room.signInRequired')}</h2>
        <p className="rlogin__desc">{t('room.signInDesc')}</p>

        {!otp ? (
          <form className="rlogin__form" onSubmit={handleSubmit} noValidate>
            <div className="rlogin__field">
              <label className="rlogin__label" htmlFor="rl-username">{t('auth.username')}</label>
              <input
                id="rl-username"
                name="username"
                type="text"
                className="rlogin__input"
                placeholder={t('auth.enterUsername')}
                value={form.username}
                onChange={handleChange}
                autoComplete="username"
              />
            </div>
            <div className="rlogin__field">
              <label className="rlogin__label" htmlFor="rl-password">{t('auth.password')}</label>
              <input
                id="rl-password"
                name="password"
                type="password"
                className="rlogin__input"
                placeholder={t('auth.enterPassword')}
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
              />
            </div>

            {error && <div className="rlogin__error">{error}</div>}

            <button type="submit" className="rlogin__btn" disabled={loading}>
              {loading ? t('auth.loadingLogin') : t('room.signInAndJoin')}
            </button>
            {onCancel && (
              <button type="button" className="rlogin__text-btn" onClick={onCancel}>
                {t('room.continueAsGuest')}
              </button>
            )}
          </form>
        ) : (
          <form className="rlogin__form" onSubmit={handleOtpSubmit} noValidate>
            <p className="rlogin__otp-desc">{t('auth.otpDesc')} {otp.maskedPhone}</p>

            {otp.displayOtpCode && (
              <div className="rlogin__otp-dev" role="status">
                <span className="rlogin__otp-dev-label">{t('auth.otpDevBanner')}</span>
                <span className="rlogin__otp-dev-code" dir="ltr">{otp.displayOtpCode}</span>
              </div>
            )}

            <div className="rlogin__field">
              <label className="rlogin__label" htmlFor="rl-otp">{t('auth.otpLabel')}</label>
              <input
                id="rl-otp"
                type="text"
                inputMode="numeric"
                maxLength={OTP_LENGTH}
                className="rlogin__input"
                value={otp.otpCode}
                onChange={(e) => setOtp({ ...otp, otpCode: e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH) })}
                autoComplete="one-time-code"
              />
            </div>

            {error && <div className="rlogin__error">{error}</div>}

            <button type="submit" className="rlogin__btn" disabled={loading}>
              {loading ? t('auth.loadingVerify') : t('auth.verifyOtp')}
            </button>
            <button type="button" className="rlogin__text-btn" onClick={handleResend}>
              {t('auth.resend')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
